import { useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquarePlus, Star, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useT } from "@/lib/i18n";

const CATS = ["general", "bug", "idea", "design"];

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [rating, setRating] = useState(0);
  const [cat, setCat] = useState("general");
  const [busy, setBusy] = useState(false);
  const location = useLocation();
  const { t } = useT();

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feedback", { message: msg, rating: rating || null, page: location.pathname, category: cat });
      toast.success(t("fb_thanks"));
      setOpen(false); setMsg(""); setRating(0); setCat("general");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid="feedback-open-button" onClick={() => setOpen(true)} title={t("fb_button")}
        className="fixed bottom-3 left-3 sm:bottom-5 sm:left-4 z-40 flex items-center gap-2 pl-2.5 pr-3 py-2 sm:pl-3 sm:pr-4 sm:py-2.5 rounded-full bg-[#0d0f16]/90 border border-white/15 text-xs font-semibold text-gray-200 shadow-lg backdrop-blur hover:border-fuchsia-400/60 hover:text-white transition-colors duration-200">
        <MessageSquarePlus className="w-4 h-4 text-fuchsia-300" /> <span className="hidden sm:inline">{t("fb_button")}</span>
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div data-testid="feedback-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9998] overflow-y-auto bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)}>
              <div className="min-h-full flex items-end sm:items-center justify-center p-4">
                <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0d0f16] p-6 shadow-2xl">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <h3 className="font-display font-black text-xl">{t("fb_title")}</h3>
                      <p className="text-xs text-gray-500 mt-0.5">{t("fb_sub")} <span className="font-mono text-gray-400">{location.pathname}</span></p>
                    </div>
                    <button data-testid="feedback-close" onClick={() => setOpen(false)} className="p-1.5 rounded-lg hover:bg-white/10"><X className="w-4 h-4" /></button>
                  </div>
                  <div className="flex gap-1.5 mb-3">
                    {CATS.map((c) => (
                      <button key={c} data-testid={`feedback-cat-${c}`} onClick={() => setCat(c)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${cat === c ? "bg-fuchsia-500/20 border-fuchsia-400/60 text-fuchsia-200" : "bg-white/5 border-white/10 text-gray-400 hover:text-white"}`}>
                        {t(`fb_cat_${c}`)}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-1 mb-3" data-testid="feedback-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} data-testid={`feedback-star-${n}`} onClick={() => setRating(n)} className="p-0.5 transition-transform duration-150 hover:scale-110">
                        <Star className={`w-6 h-6 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-gray-600"}`} />
                      </button>
                    ))}
                    <span className="text-xs text-gray-500 ml-2">{t("fb_rating_hint")}</span>
                  </div>
                  <textarea data-testid="feedback-message" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} maxLength={2000}
                    placeholder={t("fb_placeholder")}
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-fuchsia-400 transition-colors duration-200 resize-none" />
                  <button data-testid="feedback-submit" onClick={submit} disabled={busy || msg.trim().length < 3}
                    className="mt-3 w-full py-3 rounded-xl font-semibold bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:opacity-90 transition-opacity duration-200 disabled:opacity-40">
                    {busy ? t("loading") : t("fb_send")}
                  </button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
